<template>
  <v-card
    flat
    :class="[selectedOrder ? 'transparent' : 'card-bg rounded-20 mt-5 pb-5']"
  >
    <v-card v-if="selectedOrder" flat class="mx-4 mt-4 rounded-20">
      <v-card-title class="title-font"> ORDER INVOICE HISTORY </v-card-title>
    </v-card>
    <v-toolbar
      v-if="!selectedOrder"
      class="transparent py-2 pl-4 mb-4"
      flat
      dense
    >
      <v-tabs v-model="tab" class="mb-5 mt-2 transparent" light color="primary">
        <v-tab class="title-font"> Invoice History </v-tab>
        <v-tab disabled class="title-font"> Statements </v-tab>
      </v-tabs>
      <v-progress-circular
        size="20"
        indeterminate
        color="primary"
        class="mx-2"
        v-if="refreshingLinks"
      />
    </v-toolbar>
    <v-card
      flat
      :class="['mx-4 py-3 ', selectedOrder ? 'mt-4 rounded-20' : 'rounded-10']"
    >
      <v-card-text v-if="loading" class="text-center">
        <v-progress-circular indeterminate color="primary" />
      </v-card-text>
      <div v-if="tab === 0">
        <v-data-table
          :headers="invoicesHeaders"
          :items="_invoices || []"
          :items-per-page="5"
          v-if="!loading"
        >
          <template v-slot:[`header.invoice_number`]="{ header }">
            {{ header.text }}
            <v-tooltip top class="rounded-10">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="ml-1 pb-1" v-bind="attrs" v-on="on">
                  mdi-cursor-default-click
                </v-icon>
              </template>
              <span> Click ID to view Invoice Details. </span>
            </v-tooltip>
          </template>
          <template v-slot:[`header.url`]="{ header }">
            {{ header.text }}
            <v-tooltip top class="rounded-10">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="ml-1 pb-1" v-bind="attrs" v-on="on">
                  mdi-information
                </v-icon>
              </template>
              <span>
                Open to view payment details, download invoice and receipt.
              </span>
            </v-tooltip>
          </template>
          <template v-slot:[`header.refresh`]="{ header }">
            {{ header.text }}
            <v-tooltip top class="rounded-10">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="ml-1 pb-1" v-bind="attrs" v-on="on">
                  mdi-information
                </v-icon>
              </template>
              <span>
                Having trouble downloading a pdf or opening a payment page?
                Click REFRESH for new links.
              </span>
            </v-tooltip>
          </template>
          <template v-slot:[`item.invoice_number`]="{ item }">
            <div style="white-space: nowrap">
              <v-tooltip top class="rounded-10">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    text
                    small
                    v-bind="attrs"
                    v-on="on"
                    class="ml-n1 px-1"
                    @click="openDetail(item)"
                  >
                    {{ item.invoice_number }}
                  </v-btn>
                </template>
                <span> Click to view details. </span>
              </v-tooltip>
              <v-tooltip top class="rounded-10">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-if="!item.paid"
                    color="warning"
                    small
                    v-bind="attrs"
                    v-on="on"
                    :class="[
                      dueSoon.find((i) => {
                        i.uid === item.uid
                      })
                        ? 'badge-pulse'
                        : null
                    ]"
                  >
                    mdi-alert
                  </v-icon>
                </template>
                <span> This invoice has not been paid. </span>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:[`item.invoiced_at`]="{ item }">
            {{ item.invoiced_at | luxon({ output: "date_short" }) }}
          </template>
          <template v-slot:[`item.due_date`]="{ item }">
            {{ item.due_date | luxon({ output: "date_short" }) }}
          </template>
          <template v-slot:[`item.amount`]="{ item }">
            {{ formatStripeAmount(item.amount * 100, item.currency) }}
          </template>
          <template v-slot:[`item.pdf`]="{ item }">
            <a :href="item.pdf">PDF</a>
          </template>
          <template v-slot:[`item.url`]="{ item }">
            <a :href="item.url" target="_blank">Open</a>
            <v-icon class="ml-1 pb-2" color="primary" x-small>
              mdi-open-in-new
            </v-icon>
          </template>
          <template v-slot:[`item.refresh`]="{ item }">
            <v-btn
              @click="refresh(item)"
              text
              x-small
              color="secondary"
              class="px-1 py-2 off"
              :disabled="refreshedInvoices[item.invoice_number]"
            >
              <v-icon small class="mr-2"> mdi-refresh </v-icon>Refresh
            </v-btn>
          </template>
        </v-data-table>
      </div>
      <div v-if="tab === 1">
        <v-data-table
          :headers="statementHeaders"
          :items="statementRows"
          :items-per-page="5"
        >
          <template v-slot:[`item.date`]="{ item }">
            {{ item.date | luxon({ output: "date_short" }) }}
          </template>
          <template v-slot:[`item.actions`]="{ index }">
            <v-btn class="btn-primary" small @click="downloadStatement(index)">
              <v-icon class="mr-2">mdi-download</v-icon>
              Download
            </v-btn>
          </template>
        </v-data-table>
      </div>
    </v-card>
    <InvoiceDetailDialog v-model="dialog" :invoice="selectedInvoice" />
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import InvoiceDetailDialog from "@/components/shared/InvoiceDetailDialog.vue"
import client from "@/lib/ApiClient"
import { formatStripeAmount } from "@/utils"

export default {
  name: "InvoiceList",

  components: { InvoiceDetailDialog },

  data() {
    return {
      loading: false,
      refreshingLinks: false,
      dialog: false,
      selectedInvoice: null,
      refreshedInvoices: {},
      billingCycles: [],
      tab: 0,
      headers: [
        {
          text: "Invoice ID",
          value: "invoice_number"
        },
        {
          text: "Issued",
          value: "invoiced_at"
        },
        {
          text: "Amount",
          value: "amount"
        },
        {
          text: "Download (PDF)",
          value: "pdf",
          sortable: false
        },
        {
          text: "Payment Page",
          value: "url",
          sortable: false
        },
        {
          text: "Refresh Link",
          value: "refresh",
          sortable: false
        }
      ],
      statementHeaders: [
        {
          text: "Statement Date",
          value: "date"
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          align: "right"
        }
      ]
    }
  },

  watch: {
    invoices(val) {
      if (val) this.loading = false
    },

    dialog(val) {
      if (!val) this.selectedInvoice = null
    }
  },

  computed: {
    ...mapGetters([
      "invoices",
      "inArrears",
      "inboxItems",
      "selectedOrder",
      "dueSoon"
    ]),

    _invoices() {
      if (this.selectedOrder)
        return this.invoices.filter(
          (i) => i.order_uid === this.selectedOrder.uid
        )
      return this.invoices
    },

    statementRows() {
      return this.billingCycles.map((date) => ({
        date
      }))
    },

    invoicesHeaders() {
      const h = [...this.headers]

      if (this._invoices.some((i) => i.due_date)) {
        h.splice(2, 0, {
          text: "Due",
          value: "due_date"
        })
      }

      return h
    }
  },

  methods: {
    ...mapActions(["triggerReload", "setSelectedOrder", "updateInvoiceLinks"]),

    formatStripeAmount,

    openDetail(invoiceItem) {
      this.selectedInvoice = invoiceItem
      this.dialog = true
    },

    async downloadStatement(cycleIndex) {
      if (!this.billingCycles?.length) return

      let cycleStart = null
      const cycleEnd = this.billingCycles[cycleIndex]
      if (cycleIndex < this.billingCycles.length - 1)
        cycleStart = this.billingCycles[cycleIndex + 1]

      const statementBlob = await client.gateway.billing.downloadStatement(
        cycleStart,
        cycleEnd
      )

      const url = window.URL.createObjectURL(statementBlob)

      const link = document.createElement("a")
      link.href = url

      const endDate = new Date(cycleEnd).toISOString().split("T")[0]
      const fileName = `statement-${endDate}.csv`

      link.setAttribute("download", fileName)

      document.body.appendChild(link)
      link.click()

      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
    },

    async refresh(invoiceItem) {
      this.refreshingLinks = true
      if (this.refreshedInvoices[invoiceItem.invoice_number]) return
      this.$set(this.refreshedInvoices, invoiceItem.invoice_number, true)

      try {
        const invoice = await client.gateway.billing.fetchInvoice(
          invoiceItem.uid
        )

        this.updateInvoiceLinks(invoice)
      } finally {
        setTimeout(() => {
          this.$delete(this.refreshedInvoices, invoiceItem.invoice_number)
        }, 30_000)
      }
      this.refreshingLinks = false
    }
  },

  async mounted() {
    this.loading = true
    if (this.invoices) this.loading = false
    else this.triggerReload("invoices")
    // this.billingCycles = await client.gateway.billing.fetchSubscriptionCycles()
  }
}
</script>
